import { FC, useState, useRef, useEffect } from "react";
import { TextInput, Spinner } from "flowbite-react";
import { HiOutlineSearch } from "react-icons/hi";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useUserSession } from "../../context/UserContext";
import { useWorkspace } from "../../context/WorkspaceContext";

const baseUrl: string =
  process.env.REACT_APP_CLOUD_API_URL || "http://localhost:8080";

export interface AddressSearchResult {
  addressLine: string;
  postCode: string;
  city: string;
  countryCode?: string;
  placeId?: string;
}

interface AddressSuggestion {
  addressLine: string;
  placeId: string;
}

export interface AddressSearchInputProps {
  id?: string;
  name?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onSelectAddress?: (address: AddressSearchResult) => void;
  onAddressValidated?: (isValid: boolean) => void;
  onError?: (error: string) => void;
  required?: boolean;
  className?: string;
  autoFocus?: boolean;
  /**
   * Treat the initial `value` as an already-validated address (e.g. when
   * editing a previously saved stop). Prevents the field from showing the
   * "not selected" failure state until the user types again.
   */
  initiallyValidated?: boolean;
  /** Show the invalid state after the parent has attempted validation. */
  showValidationError?: boolean;
}

export const AddressSearchInput: FC<AddressSearchInputProps> = ({
  id = "address-search-field",
  name = "address-search-field",
  placeholder = "Search address...",
  value,
  onChange,
  onSelectAddress,
  onAddressValidated,
  onError,
  required = false,
  className = "",
  autoFocus = false,
  initiallyValidated = false,
  showValidationError = false,
}) => {
  const { t } = useTranslation(["common", "location"]);
  const { userSession } = useUserSession();
  const { activeWorkspace } = useWorkspace();
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isAddressSelected, setIsAddressSelected] =
    useState(initiallyValidated);
  const [showNoResults, setShowNoResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const DEBOUNCE_DELAY = 500;

  // Generate session token once per search session (for Google billing optimization)
  const getOrCreateSessionToken = () => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return sessionTokenRef.current;
  };

  // Cleanup on unmount - cancel pending requests and timeouts
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Helper to build API headers with auth token
  const getHeaders = (): Record<string, string> | null => {
    const token = userSession?.token.accessToken;
    if (!token) {
      console.warn("No auth token available");
      return null;
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    if (activeWorkspace?.workspaceId) {
      headers["X-Workspace-Id"] = activeWorkspace.workspaceId;
    }

    return headers;
  };

  const fetchAddressSuggestions = async (query: string) => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setIsSearching(true);
    setShowNoResults(false);
    try {
      const headers = getHeaders();
      if (!headers) {
        setAddressSuggestions([]);
        setIsSearching(false);
        setShowNoResults(true);
        return;
      }

      const response = await axios.post(
        `${baseUrl}/api/location/autocomplete`,
        {
          addressLine: query,
          sessionToken: getOrCreateSessionToken(), // Add session token to reduce costs
        },
        {
          headers,
          signal: abortControllerRef.current.signal,
        },
      );
      const addresses = response.data?.data || response.data?.addresses || [];
      setAddressSuggestions(addresses);

      // Show "no results" if search completed with empty results
      if (addresses.length === 0) {
        setShowNoResults(true);
      }
    } catch (err) {
      // Don't show error if request was aborted (user kept typing)
      if (axios.isCancel(err)) {
        return;
      }
      console.error("Failed to fetch address suggestions", err);
      setAddressSuggestions([]);
      setShowNoResults(true);
      if (onError) {
        onError("Failed to fetch address suggestions");
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSuggestion = async (suggestion: AddressSuggestion) => {
    onChange(suggestion.addressLine);
    setAddressSuggestions([]);
    setShowNoResults(false);
    setIsSearching(true);

    try {
      const headers = getHeaders();
      if (!headers) {
        setIsSearching(false);
        if (onError) {
          onError("Authentication required");
        }
        return;
      }

      const response = await axios.post(
        `${baseUrl}/api/location/details`,
        {
          placeId: suggestion.placeId,
          sessionToken: sessionTokenRef.current,
        },
        { headers },
      );

      // Clear session token (session complete)
      sessionTokenRef.current = null;

      const address = response.data?.data || response.data;
      if (address) {
        if (onSelectAddress) {
          onSelectAddress({
            addressLine: address.addressLine || suggestion.addressLine,
            postCode: address.postCode || "",
            city: address.city || "",
            countryCode: address.countryCode || "NO",
            placeId: address.placeId || suggestion.placeId || "",
          });
        }
        setIsAddressSelected(true);
        if (onAddressValidated) {
          onAddressValidated(true);
        }
      } else {
        // No address data received
        if (onError) {
          onError("No address details available");
        }
      }
    } catch (error) {
      console.error("Address details fetch failed:", error);
      // Keep address selected as false on error
      setIsAddressSelected(false);
      if (onAddressValidated) {
        onAddressValidated(false);
      }
      if (onError) {
        onError("Failed to fetch address details");
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleFieldChange = (newValue: string) => {
    onChange(newValue);

    // Invalidate selection when user types
    if (isAddressSelected) {
      setIsAddressSelected(false);
      if (onAddressValidated) {
        onAddressValidated(false);
      }
    }

    // Debounce API calls
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (newValue.trim().length >= 3) {
        fetchAddressSuggestions(newValue);
      } else {
        setAddressSuggestions([]);
        setShowNoResults(false);
      }
    }, DEBOUNCE_DELAY);
  };

  return (
    <div className="relative">
      <TextInput
        id={id}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={(e) => handleFieldChange(e.target.value)}
        required={required}
        className={className}
        autoComplete="nope"
        autoFocus={autoFocus}
        icon={HiOutlineSearch}
        role="combobox"
        aria-expanded={addressSuggestions.length > 0 || isSearching}
        aria-autocomplete="list"
        aria-controls={`${id}-suggestions`}
        color={
          showValidationError && required && !isAddressSelected
            ? "failure"
            : undefined
        }
      />

      {/* Suggestions dropdown */}
      {(addressSuggestions.length > 0 || isSearching || showNoResults) && (
        <div className="absolute w-full z-50 mt-1">
          <ul
            id={`${id}-suggestions`}
            role="listbox"
            className="border border-border-strong max-h-60 overflow-y-auto bg-surface-input rounded-lg shadow-lg"
          >
            {isSearching && (
              <li
                role="status"
                aria-live="polite"
                className="flex items-center justify-center gap-2 py-4 text-sm text-muted"
              >
                <Spinner size="sm" />
                <span>{t("location:searching_addresses")}</span>
              </li>
            )}
            {!isSearching && addressSuggestions.length > 0 && (
              <li className="px-3 py-2 text-xs text-muted border-b border-gray-100 dark:border-gray-600 bg-surface-muted">
                {t("location:select_address_to_autofill")}
              </li>
            )}
            {!isSearching &&
              showNoResults &&
              addressSuggestions.length === 0 && (
                <li className="px-3 py-3 text-sm text-muted text-center">
                  {t("location:no_addresses_found")}
                </li>
              )}
            {!isSearching &&
              addressSuggestions.map((s, idx) => (
                <li
                  key={s.placeId || idx}
                  role="option"
                  aria-selected="false"
                  tabIndex={0}
                  className="px-3 py-2.5 hover:bg-brand-muted dark:hover:bg-gray-600 cursor-pointer transition-colors duration-150 border-b border-gray-100 dark:border-gray-600 last:border-0 text-sm dark:text-white focus:bg-brand-muted dark:focus:bg-gray-600 focus:outline-none"
                  onMouseDown={() => handleSelectSuggestion(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelectSuggestion(s);
                    }
                  }}
                >
                  {s.addressLine}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
};
